// appIcons.tsx — one source of truth for the app's iconography.
//
// Soft, consistent Lucide line icons, each tied to a muted earthy hue so a
// destination is recognisable by its colour at a glance — colourful but calm.

import {
  Home,
  BookOpen,
  Feather,
  Leaf,
  Users,
  NotebookPen,
  UserRound,
  HeartHandshake,
  Heart,
  Footprints,
  Sparkles,
} from "lucide-react";

// Muted, nature-derived palette. Low saturation keeps the calm.
export const hues = {
  sage: "#5f8059",
  clay: "#a9673f",
  ochre: "#a17832",
  dustyBlue: "#5d7895",
  teal: "#4f7d75",
  rose: "#9c5a6a",
  ink: "#6e6861",
} as const;

export type IconSpec = { Icon: React.ComponentType<any>; color: string };

// Named destinations / concepts → their icon + hue.
export const icons: Record<string, IconSpec> = {
  home: { Icon: Home, color: hues.sage },
  book: { Icon: BookOpen, color: hues.clay },
  breaths: { Icon: Leaf, color: hues.teal },
  reflect: { Icon: Feather, color: hues.ochre },
  connect: { Icon: Users, color: hues.dustyBlue },
  note: { Icon: NotebookPen, color: hues.rose },
  author: { Icon: UserRound, color: hues.rose },
  echoes: { Icon: Sparkles, color: hues.ochre },
  offer: { Icon: HeartHandshake, color: hues.teal },
  warmth: { Icon: Heart, color: hues.rose },
  walk: { Icon: Footprints, color: hues.sage },
};

/** Render a named app icon at a given size (colour comes from the map). */
export const AppIcon = ({
  name,
  size = 20,
  className = "",
}: {
  name: keyof typeof icons | string;
  size?: number;
  className?: string;
}) => {
  const spec = icons[name];
  if (!spec) return null;
  const { Icon, color } = spec;
  return <Icon size={size} color={color} strokeWidth={1.9} className={className} aria-hidden="true" />;
};
